'use client'

import { useEffect, useState } from 'react'
import { LinkedinLogo, XLogo, Link as LinkIcon, Check } from '@phosphor-icons/react'

/* Dual-weight icon: shows outlined by default, filled on parent hover */
function HoverFillIcon({ regular, filled }: { regular: React.ReactNode; filled: React.ReactNode }) {
  return (
    <span className="blog-share-hfi" aria-hidden="true">
      <span className="blog-share-hfi__reg">{regular}</span>
      <span className="blog-share-hfi__fill">{filled}</span>
    </span>
  )
}

interface BlogShareInlineProps {
  title?: string
}

export function BlogShareInline({ title }: BlogShareInlineProps) {
  const [url, setUrl] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (typeof window !== 'undefined') setUrl(window.location.href)
  }, [])

  const handleCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url)
      } else {
        const ta = document.createElement('textarea')
        ta.value = url
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
    } catch {
      /* swallow — toast still shows for user feedback */
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const linkedinHref = url
    ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    : '#'
  const xHref = url
    ? `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}${title ? `&text=${encodeURIComponent(title)}` : ''}`
    : '#'

  return (
    <div className="blog-share-inline" role="group" aria-label="Share article">
      <a
        href={linkedinHref}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-icon-only btn-icon-only--sm btn-icon-only--outline"
        aria-label="Share on LinkedIn"
      >
        <HoverFillIcon
          regular={<LinkedinLogo size={16} weight="regular" />}
          filled={<LinkedinLogo size={16} weight="fill" />}
        />
      </a>
      <a
        href={xHref}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-icon-only btn-icon-only--sm btn-icon-only--outline"
        aria-label="Share on X"
      >
        <HoverFillIcon
          regular={<XLogo size={16} weight="regular" />}
          filled={<XLogo size={16} weight="fill" />}
        />
      </a>
      <span className="blog-share-copy-wrap">
        <button
          type="button"
          className="btn-icon-only btn-icon-only--sm btn-icon-only--outline"
          onClick={handleCopy}
          aria-label={copied ? 'Link copied' : 'Copy link'}
        >
          {copied ? <Check size={16} weight="bold" /> : <LinkIcon size={16} weight="regular" />}
        </button>
        <span className={`blog-share-tooltip${copied ? ' blog-share-tooltip--visible' : ''}`} aria-hidden="true">
          Link copied!
        </span>
      </span>
    </div>
  )
}
